import {
  Autocomplete,
  Button,
  Card,
  Fade,
  Grid,
  InputAdornment,
  Modal,
  Switch,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CustomTextField from "components/Form/CustomTextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import useDropdowns from "hooks/useDropdowns";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import invoiceRowSchema from "./invoiceRowSchema";

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "25%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const AddInvoiceRowModal = ({
  open,
  setOpen,
  onAddRow,
  row,
  isShiftJob,
  isDetails,
  positions,
  shifts,
  employees,
}) => {
  const classes = useStyle();

  const [shiftOptions, shiftIndexed] = useMemo(() => {
    const result = (shifts ?? []).map((shf) => ({
      label: shf.shiftName,
      value: shf.slug,
    }));
    const indexed = result.reduce((prev, current, index) => {
      // eslint-disable-next-line no-param-reassign
      prev[current.value] = current;
      return prev;
    }, {});
    return [result, indexed];
  }, [shifts]);

  const [employeeOptions, employeeIndexed] = useMemo(() => {
    const result = (employees ?? []).map((emp) => ({
      label: `${emp.firstName} ${emp.lastName}`,
      value: emp._id,
    }));
    const indexed = result.reduce((prev, current, index) => {
      // eslint-disable-next-line no-param-reassign
      prev[current.value] = current;
      return prev;
    }, {});
    return [result, indexed];
  }, [employees]);

  const resolver = useYupValidationResolver(invoiceRowSchema(isShiftJob, isDetails));

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    getValues,
    formState: { dirtyFields, errors },
  } = useForm({
    resolver,
    defaultValues: {
      applicantId: row?.applicantId ?? "",
      positionName: row?.positionName ?? "",
      shiftSlug: row?.shiftSlug ?? "",
      totalEmployees: row?.totalEmployees ?? 1,
    },
  });

  const selectedShift = shiftIndexed[watch("shiftSlug")];

  const [positionOptions, positionsIndexed] = useMemo(() => {
    let positionsLocal = positions || [];
    if (isShiftJob) {
      if (selectedShift) {
        const selectedShiftSlug = selectedShift.value;
        const selectedShiftObject = shifts.find((sh) => sh.slug === selectedShiftSlug);
        if (selectedShiftObject) {
          positionsLocal = selectedShiftObject.positions;
        }
      } else {
        positionsLocal = [];
      }
    }

    const result = positionsLocal.map((pos) => ({
      label: pos.positionName,
      value: pos.positionName,
    }));
    const indexed = result.reduce((prev, current, index) => {
      // eslint-disable-next-line no-param-reassign
      prev[current.value] = current;
      return prev;
    }, {});
    return [result, indexed];
  }, [isShiftJob, positions, selectedShift, shifts]);

  const handleSubmitFunc = (values) => {
    const params = {};

    if (isDetails) {
      params.applicantId = values.applicantId;
      params.positionName = values.positionName;
      const currentEmployee = employees?.find((emp) => emp._id === values.applicantId);
      if (currentEmployee) {
        params.firstName = currentEmployee.firstName;
        params.lastName = currentEmployee.lastName;
        params.email = currentEmployee.email;
        params.altEmail = currentEmployee.altEmail;
        params.phone = currentEmployee.phone;
        params.altPhone = currentEmployee.altPhone;
        params.employeeID = currentEmployee.employeeID;
        params.partnerSlug = currentEmployee.partnerSlug;
        if (!isShiftJob) {
          params.positionName = currentEmployee.positionName;
        }
      }

      if (isShiftJob) {
        params.shiftSlug = values.shiftSlug;

        const currentShift = values.shiftSlug
          ? shifts?.find((shft) => shft.slug === values.shiftSlug)
          : null;
        if (currentShift) {
          params.shiftName = currentShift.shiftName;
        }
      }
    } else {
      params.positionName = values.positionName;
      params.totalEmployees = values.totalEmployees ? parseInt(values.totalEmployees, 10) : 1;
    }

    if (Object.keys(row ?? {}).length > 0) {
      // Row provided. This is an edit
      params.row = row;
    }

    onAddRow(params);
    setOpen(false);
  };

  const cleanInput = (value) => {
    // Remove any character that's not a digit or decimal point
    const cleaned = value.replace(/[^\d]/g, "");

    return cleaned;
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Fade in={open}>
        <Card className={[classes.container].join(" ")}>
          <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }} key="text_addNote">
            Invoice Line
          </MDTypography>
          <MDBox p={3}>
            <form onSubmit={handleSubmit(handleSubmitFunc)}>
              <Grid container xs={12} rowSpacing={3} columnSpacing={2}>
                {isDetails && (
                  <Grid item xs={12}>
                    <Controller
                      name="applicantId"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={employeeOptions}
                          autoSelect
                          value={employeeIndexed[field.value] ?? ""}
                          onChange={(e, v) => {
                            setValue("applicantId", v?.value ?? "");
                          }}
                          sx={{ maxHeight: "44px", width: "100%" }}
                          renderInput={(params) => (
                            <CustomTextField
                              type="text"
                              label="Employee"
                              showError
                              {...params}
                              InputLabelProps={{ shrink: true }}
                              errorMessage={error?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                )}
                {(!isDetails || isShiftJob) && (
                  <Grid item xs={12}>
                    <Controller
                      name="positionName"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={positionOptions}
                          autoSelect
                          value={positionsIndexed[field.value] ?? ""}
                          onChange={(e, v) => {
                            setValue("positionName", v?.value ?? "");
                          }}
                          sx={{ maxHeight: "44px", width: "100%" }}
                          renderInput={(params) => (
                            <CustomTextField
                              type="text"
                              label="Position"
                              showError
                              {...params}
                              InputLabelProps={{ shrink: true }}
                              errorMessage={error?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                )}
                {isShiftJob && isDetails && (
                  <Grid item xs={12}>
                    <Controller
                      name="shiftSlug"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={shiftOptions}
                          autoSelect
                          value={shiftIndexed[field.value] ?? ""}
                          onChange={(e, v) => {
                            setValue("shiftSlug", v?.value ?? "");
                          }}
                          sx={{ maxHeight: "44px", width: "100%" }}
                          renderInput={(params) => (
                            <CustomTextField
                              type="text"
                              label="Shift"
                              showError
                              {...params}
                              InputLabelProps={{ shrink: true }}
                              errorMessage={error?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                )}
                {!isDetails && (
                  <Grid item xs={6}>
                    <Controller
                      name="totalEmployees"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <CustomTextField
                          {...field}
                          onChange={(e) => {
                            const val = cleanInput(e.target.value ?? "");
                            setValue("totalEmployees", val);
                          }}
                          label="Staff Quantity"
                          InputLabelProps={{ shrink: true }}
                          showError
                          errorMessage={error?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
              <Grid container mt={2} justifyContent="flex-end">
                <Grid container item width="auto" spacing={3} justifyContent="flex-end">
                  <Grid item xs={6}>
                    <Button
                      color="error"
                      variant="contained"
                      fullWidth
                      onClick={async (e) => {
                        e?.stopPropagation();
                        setOpen(false);
                      }}
                    >
                      <MDTypography variant="body2" color="white">
                        Cancel
                      </MDTypography>
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button type="submit" variant="contained" fullWidth>
                      <MDTypography variant="body2" color="white">
                        Save
                      </MDTypography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Card>
      </Fade>
    </Modal>
  );
};

export default AddInvoiceRowModal;
